import { DatePicker, Dropdown } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  billExportUrl,
  useBillBreakdown,
  useBillOps,
  useBillSeries,
  useBillSummary,
  type Period,
} from '@entities/billing';
import { useHome } from '@entities/home';
import { TopupModal } from '@features/topup';
import { extractErrorMessage, httpClient } from '@shared/api';
import { addDays, num, rangeText, rub, StepChart, TopBar, type ChartPoint } from '@shared/ui/v2';
import './billing-v2.css';

const MSK = 'Europe/Moscow';
const todayMsk = () => new Date().toLocaleDateString('sv-SE', { timeZone: MSK });

const PRESETS = [
  { key: 'week', label: 'Неделя', days: 7 },
  { key: 'month', label: 'Месяц', days: 30 },
  { key: 'q', label: '3 месяца', days: 90 },
  { key: 'all', label: 'Всё время', days: 3650 },
] as const;

const SERIES = [
  { key: 'total', label: 'Всего', color: '#1F2937' },
  { key: 'answers', label: 'Ответы', color: '#2563EB' },
  { key: 'kb', label: 'База знаний', color: '#D97706' },
];

const KINDS = [
  { key: 'all', label: 'Все операции' },
  { key: 'answers', label: 'Ответы' },
  { key: 'kb', label: 'База знаний' },
  { key: 'topups', label: 'Пополнения' },
];

function whenOp(iso: string) {
  const d = new Date(iso);
  return `${d.toLocaleDateString('ru-RU', { timeZone: MSK, day: 'numeric', month: 'short' }).replace('.', '')}, ${d.toLocaleTimeString('ru-RU', { timeZone: MSK, hour: '2-digit', minute: '2-digit' })}`;
}

const signed = (v: string | number) => {
  const n = Number(v);
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${rub(Math.abs(n))}`;
};

/** Экран 14 «Баланс и расход» (утверждён 28.10.2026). Период — общий для всех блоков. */
export function BillingV2Page() {
  const [sp, setSp] = useSearchParams();
  const today = useMemo(todayMsk, []);
  const [preset, setPreset] = useState<string | null>('week');
  const [range, setRange] = useState<Period>({ from: addDays(today, -6), to: today });
  const [step, setStep] = useState<'day' | 'hour'>('day');
  const [by, setBy] = useState<'model' | 'assistant' | 'channel'>('model');
  const [opAssistant, setOpAssistant] = useState<string | null>(null);
  const [opKind, setOpKind] = useState('all');
  const [topupOpen, setTopupOpen] = useState(sp.get('topup') === '1');
  const { data: home } = useHome();

  const { data: s, error: sErr } = useBillSummary(range);
  const { data: series, isFetching: seriesFetching } = useBillSeries(range, step);
  const { data: rows, error: rowsErr } = useBillBreakdown(range, by);
  // Пока сводка не пришла (или не загрузилась) — прочерк, а не «0,00 ₽» на месте баланса.
  const sr = (v: string | number | null | undefined) => (v == null ? '—' : rub(v));
  const ops = useBillOps(range, opAssistant, opKind);
  const opItems = useMemo(() => ops.data?.pages.flatMap((pg) => pg.items) ?? [], [ops.data]);
  const opTotal = ops.data?.pages[0]?.total ?? 0;
  const firstDay = series?.overview[0]?.t;

  const pickPreset = (key: string, days: number) => {
    setPreset(key);
    const from =
      key === 'all' ? (firstDay ?? addDays(today, -days + 1)) : addDays(today, -days + 1);
    setRange({ from, to: today });
    setStep('day');
  };
  const setCustom = (r: Period) => {
    setPreset(null);
    // По часам сервер отдаёт не больше 7 дней — сужаем период, чтобы график и цифры совпадали.
    setRange(
      step === 'hour' && r.from < addDays(r.to, -6) ? { from: addDays(r.to, -6), to: r.to } : r,
    );
  };

  const sentinel = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = ops;
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, opItems.length]);

  const spend = Number(s?.spend_rub ?? 0);
  const prev = Number(s?.prev_spend_rub ?? 0);
  const delta = prev > 0 ? Math.round(((spend - prev) / prev) * 100) : null;
  const periodTxt = rangeText(range.from, range.to);
  const asstName = home?.assistants.find((a) => a.id === opAssistant)?.name;

  return (
    <>
      <TopBar title="Баланс и расход" hint="Один баланс на все ассистенты и API" />
      <div className="v2-content bl-wrap">
        <div className="bl-period">
          <span className="v2-small v2-muted">Период:</span>
          <div className="bl-seg" role="radiogroup" aria-label="Период">
            {PRESETS.map((pr) => (
              <button
                key={pr.key}
                type="button"
                role="radio"
                aria-checked={preset === pr.key}
                className={preset === pr.key ? 'is-on' : undefined}
                onClick={() => pickPreset(pr.key, pr.days)}
              >
                {pr.label}
              </button>
            ))}
          </div>
          <DatePicker.RangePicker
            className="bl-dates"
            format="D MMM YYYY"
            allowClear={false}
            value={[dayjs(range.from), dayjs(range.to)]}
            disabledDate={(d) => d.isAfter(dayjs(today), 'day')}
            onChange={(v) => {
              if (v?.[0] && v[1])
                setCustom({ from: v[0].format('YYYY-MM-DD'), to: v[1].format('YYYY-MM-DD') });
            }}
          />
        </div>

        {sErr && (
          <div role="alert" className="v2-card v2-card-pad">
            Не удалось загрузить сводку: {extractErrorMessage(sErr)}
          </div>
        )}
        <div className="bl-tiles">
          <section className="v2-card bl-tile" aria-label="Баланс">
            <div className="bl-tile-head">
              <span className="v2-xs v2-muted">Баланс</span>
              <button
                type="button"
                className="v2-btn is-primary bl-mini"
                onClick={() => setTopupOpen(true)}
              >
                Пополнить
              </button>
            </div>
            <b className="bl-big">{sr(s?.balance_rub)}</b>
            {Number(s?.bonus_left_rub ?? 0) > 0 && (
              <span className="v2-xs v2-muted">в т. ч. бонус {sr(s?.bonus_left_rub)}</span>
            )}
            <span className="v2-xs v2-muted">
              {s?.notify_low_balance ? (
                <>
                  напомним, когда останется &lt; {rub(s.low_balance_threshold).replace(',00', '')} ·{' '}
                  <Link to="/settings#notifications">изменить</Link>
                </>
              ) : (
                <Link to="/settings#notifications">Напомнить, когда баланс заканчивается</Link>
              )}
            </span>
          </section>
          <section className="v2-card bl-tile" aria-label="Расход за период">
            <span className="v2-xs v2-muted">Расход за период</span>
            <b className="bl-big">{sr(s?.spend_rub)}</b>
            {delta != null && (
              <span className={delta > 0 ? 'bl-up' : 'bl-down'}>
                {delta > 0 ? '▲' : '▼'} {Math.abs(delta)}% к прошлому периоду
              </span>
            )}
            <span className="v2-xs v2-muted">
              ответы {sr(s?.spend_answers_rub)} · база знаний {sr(s?.spend_kb_rub)}
            </span>
          </section>
          <section className="v2-card bl-tile" aria-label="Ответов за период">
            <span className="v2-xs v2-muted">Ответов за период</span>
            <b className="bl-big">{num(s?.answers)}</b>
            {s?.avg_answer_rub && (
              <span className="v2-xs v2-muted">в среднем {rub(s.avg_answer_rub)} за ответ</span>
            )}
          </section>
          <section className="v2-card bl-tile" aria-label="Пополнения за период">
            <span className="v2-xs v2-muted">Пополнения за период</span>
            <b className="bl-big">{sr(s?.topups_rub)}</b>
            <span className="v2-xs v2-muted">
              {Number(s?.topups_rub ?? 0) === 0
                ? 'пополнений не было'
                : [
                    Number(s?.topups_paid_rub) && `оплаты ${sr(s?.topups_paid_rub)}`,
                    Number(s?.topups_bonus_rub) && `бонус ${sr(s?.topups_bonus_rub)}`,
                    Number(s?.topups_adjust_rub) && `корректировки ${sr(s?.topups_adjust_rub)}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
            </span>
          </section>
        </div>

        <section className="v2-card bl-chart">
          <StepChart
            title="График"
            series={SERIES}
            points={(series?.points ?? []) as unknown as ChartPoint[]}
            overview={(series?.overview ?? []) as unknown as ChartPoint[]}
            overviewKey="total"
            step={step}
            onStep={(st) => {
              setStep(st);
              if (st === 'hour') setCustom({ from: addDays(today, -2), to: today });
            }}
            range={range}
            onRange={setCustom}
            today={today}
            fetching={seriesFetching}
            fmt={(v) =>
              `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: v < 10 ? 2 : 0 }).format(v)} ₽`
            }
            integer={false}
            emptyText="За этот период списаний нет"
            headExtra={
              <a
                className="v2-btn"
                href={billExportUrl(range)}
                download
                onClick={async (e) => {
                  // Ссылка идёт мимо автообновления входа: сначала любой запрос через
                  // httpClient — он обновит истёкший токен, потом скачиваем.
                  e.preventDefault();
                  const url = e.currentTarget.href;
                  await httpClient.get('/account').catch(() => undefined);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = '';
                  a.click();
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  aria-hidden="true"
                >
                  <path d="M12 4v12M7 11l5 5 5-5M4 20h16" />
                </svg>
                Скачать детализацию
              </a>
            }
          />
        </section>

        <section className="v2-card bl-card" aria-labelledby="h-spend">
          <div className="bl-card-head">
            <h2 id="h-spend" className="v2-h2">
              Расход <span className="v2-muted bl-period-txt">· {periodTxt}</span>
            </h2>
            <div className="bl-tabs" role="tablist">
              {(
                [
                  ['model', 'По моделям'],
                  ['assistant', 'По ассистентам'],
                  ['channel', 'По каналам'],
                ] as const
              ).map(([k, t]) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={by === k}
                  className={by === k ? 'is-on' : undefined}
                  onClick={() => setBy(k)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="v2-table-wrap">
            <table className="v2-table bl-table">
              <thead>
                <tr>
                  <th scope="col">
                    {by === 'model' ? 'Модель' : by === 'assistant' ? 'Ассистент' : 'Канал'}
                  </th>
                  <th scope="col">Ответов</th>
                  <th scope="col">Токены, вход / выход</th>
                  <th scope="col" style={{ textAlign: 'right' }}>
                    Сумма
                  </th>
                </tr>
              </thead>
              <tbody>
                {(rows ?? []).map((r) => (
                  <tr key={r.name}>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td>{r.answers == null ? '—' : num(r.answers)}</td>
                    <td>
                      {r.tokens_in == null ? '—' : `${num(r.tokens_in)} / ${num(r.tokens_out)}`}
                    </td>
                    <td style={{ textAlign: 'right' }}>{rub(r.sum_rub)}</td>
                  </tr>
                ))}
                {rowsErr && (
                  <tr>
                    <td colSpan={4} className="v2-muted" role="alert">
                      Не удалось загрузить: {extractErrorMessage(rowsErr)}
                    </td>
                  </tr>
                )}
                {rows && !rows.length && (
                  <tr>
                    <td colSpan={4} className="v2-muted">
                      За этот период списаний не было.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="v2-card bl-card" aria-labelledby="h-ops">
          <div className="bl-card-head">
            <h2 id="h-ops" className="v2-h2">
              История операций <span className="v2-muted bl-period-txt">· {periodTxt}</span>
            </h2>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Dropdown
                trigger={['click']}
                menu={{
                  selectedKeys: [opAssistant ?? 'all'],
                  items: [
                    { key: 'all', label: 'Все ассистенты' },
                    ...(home?.assistants ?? []).map((a) => ({ key: a.id, label: a.name })),
                  ],
                  onClick: ({ key }) => setOpAssistant(key === 'all' ? null : key),
                }}
              >
                <button type="button" className="v2-btn">
                  {asstName ?? 'Все ассистенты'} ▾
                </button>
              </Dropdown>
              <Dropdown
                trigger={['click']}
                menu={{
                  selectedKeys: [opKind],
                  items: KINDS.map((k) => ({ key: k.key, label: k.label })),
                  onClick: ({ key }) => setOpKind(key),
                }}
              >
                <button type="button" className="v2-btn">
                  {KINDS.find((k) => k.key === opKind)?.label} ▾
                </button>
              </Dropdown>
            </div>
          </div>
          {ops.error && (
            <div className="v2-card-pad v2-small">
              Не удалось загрузить операции: {extractErrorMessage(ops.error)}
            </div>
          )}
          <div className="v2-table-wrap bl-ops">
            <table className="v2-table bl-table">
              <thead>
                <tr>
                  <th scope="col">Когда</th>
                  <th scope="col">Операция</th>
                  <th scope="col">Подробности</th>
                  <th scope="col" style={{ textAlign: 'right' }}>
                    Сумма
                  </th>
                  <th scope="col" style={{ textAlign: 'right' }}>
                    Баланс
                  </th>
                </tr>
              </thead>
              <tbody>
                {opItems.map((o) => (
                  <tr key={o.id}>
                    <td>{whenOp(o.at)}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{o.title}</div>
                      <div className="v2-xs v2-muted">{o.who}</div>
                    </td>
                    <td className="bl-details">{o.details || '—'}</td>
                    <td
                      style={{ textAlign: 'right', fontWeight: 600 }}
                      className={Number(o.amount_rub) > 0 ? 'bl-plus' : undefined}
                    >
                      {signed(o.amount_rub)}
                    </td>
                    <td style={{ textAlign: 'right' }} className="v2-muted">
                      {rub(o.balance_after_rub)}
                    </td>
                  </tr>
                ))}
                {!ops.isLoading && !opItems.length && (
                  <tr>
                    <td colSpan={5} className="v2-muted">
                      За этот период операций нет.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div ref={sentinel} style={{ height: 1 }} />
          </div>
          <div className="v2-xs v2-muted bl-foot">
            {opTotal > opItems.length
              ? `Показаны последние ${num(opItems.length)} из ${num(opTotal)} операций за период; остальные — при прокрутке или в детализации.`
              : `Операций за период: ${num(opTotal)}.`}{' '}
            Время — московское. Уточнение вопроса и ответ показаны одной строкой.
          </div>
        </section>
      </div>
      <TopupModal
        open={topupOpen}
        onClose={() => {
          setTopupOpen(false);
          if (sp.get('topup')) {
            sp.delete('topup');
            setSp(sp, { replace: true });
          }
        }}
      />
    </>
  );
}
